import { useState } from "react";
import { Card, Badge } from "@daycal/ui";
import {
  FiCopy,
  FiCheck,
  FiKey,
  FiGlobe,
  FiLock,
  FiUser,
  FiCalendar,
  FiClock,
  FiFileText,
  FiPercent,
  FiList,
  FiLink,
  FiServer,
  FiUsers,
  FiShield,
  FiSmartphone,
  FiSettings,
  FiDownload,
} from "react-icons/fi";

// Full API reference (static). Documents EVERY endpoint in apps/api/src/http/routes/index.js,
// grouped by CONSUMER: Host API, Super Admin API, Public Booking API, System. Each endpoint
// carries a REAL response sample verified against the controllers/services/DB schema. An
// external team uses this to build a mobile app / WordPress plugin / any integration. This
// page only DESCRIBES the API — keep it in sync with routes/index.js, the Zod schemas in
// packages/shared/src/schemas, and the controller responses. Do not document routes that
// aren't implemented.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080";
const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL || "https://<your-project>.supabase.co";

// --- Auth types → badge -----------------------------------------------------------------
// jwt   : Supabase-Auth user JWT (the main app API)
// token : API v1 Bearer token (server-to-server, read-only)
// admin : user JWT that must belong to a SUPER_ADMIN
// public: no auth
// system: infra only — not called by client apps
const AUTH_META = {
  jwt: { label: "User JWT", tone: "brand" },
  token: { label: "API Token", tone: "success" },
  admin: { label: "Super Admin", tone: "danger" },
  public: { label: "Public", tone: "neutral" },
  system: { label: "System only", tone: "warning" },
};

function methodTone(method) {
  if (method === "GET") return "brand";
  if (method === "POST") return "success";
  if (method === "PATCH" || method === "PUT") return "warning";
  if (method === "DELETE") return "danger";
  return "neutral";
}

// --- Reusable example fragments ---------------------------------------------------------

const EX_USER = `{
  "user": {
    "id": "3f9a1c22-...",
    "username": "acme",
    "email": "host@example.com",
    "name": "Acme Co",
    "role": "USER",
    "status": "active",
    "plan_id": "b2c7...",
    "avatar_url": "https://.../avatars/3f9a/avatar.png",
    "brand_name": "Acme",
    "phone": "+919812345678",
    "timezone": "Asia/Kolkata",
    "locale": "en",
    "custom_domain": null,
    "plan_name": "Pro",
    "max_event_types": -1,
    "allow_custom_domains": true,
    "allow_google_calendar_sync": true,
    "allow_paid_bookings": true,
    "allow_api_access": true,
    "allow_mobile_app": true,
    "allow_teams": false,
    "max_employee_seats": 0
  }
}`;

const EX_EVENT_TYPE_ROW = `{
      "id": "e1d2...",
      "host_id": "3f9a...",
      "title": "30 Minute Meeting",
      "slug": "30-min-meeting",
      "duration_min": 30,
      "buffer_before_min": 0,
      "buffer_after_min": 0,
      "advance_booking_days": 30,
      "location_type": "online",
      "description": "A 30-minute meeting.",
      "require_payment": true,
      "price_minor": 1000,
      "currency": "INR",
      "status": "active",
      "created_at": "2026-08-01T09:00:00.000Z",
      "updated_at": "2026-08-01T09:00:00.000Z"
    }`;

const EX_BOOKING_ROW = `{
      "id": "bk_7a8b...",
      "host_id": "3f9a...",
      "event_type_id": "e1d2...",
      "invitee_name": "Jane Doe",
      "invitee_email": "jane@example.com",
      "invitee_timezone": "Asia/Kolkata",
      "start_utc": "2026-09-01T04:30:00.000Z",
      "end_utc": "2026-09-01T05:00:00.000Z",
      "status": "confirmed",
      "original_price_minor": 1000,
      "paid_amount_minor": 1000,
      "currency": "INR",
      "location_type": "online",
      "meet_url": null,
      "created_at": "2026-08-20T10:00:00.000Z"
    }`;

const EX_PROMO_ROW = `{
      "id": "pr_9f...",
      "scope": "host_appointment",
      "host_id": "3f9a...",
      "code": "WELCOME10",
      "discount_type": "percentage",
      "discount_value": "10.00",
      "applicable_to": "all",
      "max_uses": 100,
      "used_count": 4,
      "expires_at": null,
      "is_disabled": false,
      "created_at": "2026-08-05T09:00:00.000Z"
    }`;

const EX_DOMAIN = `{
  "custom_domain": "book.acme.com",
  "verified": false,
  "dns_record": {
    "type": "TXT",
    "host": "book.acme.com",
    "value": "daycal-verification=8f2c1a..."
  }
}`;

const EX_AVAILABILITY = `{
  "schedule": {
    "id": "sch_1a...",
    "host_id": "3f9a...",
    "name": "Default",
    "timezone": "Asia/Kolkata",
    "is_default": true
  },
  "rules": [
    {
      "id": "r_mon",
      "schedule_id": "sch_1a...",
      "day_of_week": 1,
      "enabled": true,
      "start_time": "09:00:00",
      "end_time": "17:00:00",
      "breaks": [
        { "id": "b_1", "break_start": "13:00:00", "break_end": "14:00:00" }
      ]
    }
  ],
  "overrides": [
    {
      "id": "ov_1",
      "schedule_id": "sch_1a...",
      "override_date": "2026-08-15",
      "is_unavailable": true,
      "start_time": null,
      "end_time": null
    }
  ]
}`;

// --- Endpoint catalogue, grouped by CONSUMER --------------------------------------------

const HOST_GROUPS = [
  {
    id: "account",
    icon: FiUser,
    title: "Account & profile",
    endpoints: [
      {
        method: "GET",
        path: "/me",
        auth: "jwt",
        summary: "Current user profile + plan + feature flags.",
        example: EX_USER,
      },
      {
        method: "PATCH",
        path: "/me",
        auth: "jwt",
        summary: "Update own profile.",
        body: [
          ["name", "string?", "1–120."],
          ["username", "string?", "Public handle (409 if taken)."],
          ["brand_name", "string?", "Nullable."],
          ["phone", "string?", "Free-form, ≤32. Nullable."],
          ["timezone", "string?", "IANA tz."],
          ["locale", "string?", "Supported locale."],
        ],
        example: `{ "user": { "id": "3f9a...", "username": "acme", "name": "Acme Co", "timezone": "Asia/Kolkata", "locale": "en", "updated_at": "2026-08-22T10:00:00.000Z" } }`,
      },
      {
        method: "POST",
        path: "/me/avatar",
        auth: "jwt",
        summary:
          'Upload avatar. multipart/form-data, field "file", image ≤5 MB.',
        example: `{ "user": { "id": "3f9a...", "avatar_url": "https://.../avatars/3f9a/avatar-1756800000.png" }, "avatar_url": "https://.../avatars/3f9a/avatar-1756800000.png" }`,
      },
    ],
  },
  {
    id: "event-types",
    icon: FiCalendar,
    title: "Event types (services)",
    blurb: "Create is limited by the plan’s max_event_types.",
    endpoints: [
      {
        method: "GET",
        path: "/event-types",
        auth: "jwt",
        summary: "List the host’s event types.",
        example: `{ "event_types": [ ${EX_EVENT_TYPE_ROW} ] }`,
      },
      {
        method: "POST",
        path: "/event-types",
        auth: "jwt",
        summary: "Create an event type.",
        feature: "max_event_types",
        body: [
          ["title", "string", "1–160."],
          ["slug", "string", "Lowercase, unique per host."],
          ["duration_min", "number", "5–1440, default 30."],
          [
            "buffer_before_min / buffer_after_min",
            "number",
            "0–1440, default 0.",
          ],
          ["advance_booking_days", "number", "0–365, default 30."],
          ["location_type", "enum", "online | in_person | phone | custom."],
          ["description", "string?", "≤2000. Nullable."],
          ["require_payment", "boolean", "Default false."],
          [
            "price_minor",
            "number?",
            "Minor units (paise). Required if require_payment.",
          ],
          ["currency", "string", "3-letter, default INR."],
          ["status", "enum", "active | inactive."],
          [
            "redirect_url",
            "string?",
            "Absolute http(s) post-booking redirect. Nullable.",
          ],
        ],
        example: `// 201 Created\n{ "event_type": ${EX_EVENT_TYPE_ROW} }`,
      },
      {
        method: "PATCH",
        path: "/event-types/:id",
        auth: "jwt",
        summary: "Update an event type (any subset of the create fields).",
        example: `{ "event_type": ${EX_EVENT_TYPE_ROW} }`,
      },
      {
        method: "DELETE",
        path: "/event-types/:id",
        auth: "jwt",
        summary: "Delete an event type.",
        example: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "availability",
    icon: FiClock,
    title: "Availability",
    endpoints: [
      {
        method: "GET",
        path: "/availability",
        auth: "jwt",
        summary: "Weekly schedule + timezone + overrides.",
        example: EX_AVAILABILITY,
      },
      {
        method: "PUT",
        path: "/availability",
        auth: "jwt",
        summary: "Replace the weekly schedule. Returns the full schedule.",
        body: [
          ["name", "string", 'Schedule name, default "Default".'],
          ["timezone", "string", "IANA, default Asia/Kolkata."],
          ["is_default", "boolean", "Default true."],
          [
            "rules",
            "array",
            "Up to 7: { day_of_week 0–6, enabled, start_time HH:MM, end_time HH:MM, breaks:[{break_start,break_end}] }.",
          ],
        ],
        example: EX_AVAILABILITY,
      },
      {
        method: "POST",
        path: "/availability/overrides",
        auth: "jwt",
        summary:
          "Add/replace a single date override (day off or special hours).",
        body: [
          ["override_date", "string", "YYYY-MM-DD."],
          ["is_unavailable", "boolean", "Default true (day off)."],
          [
            "start_time / end_time",
            "string?",
            "HH:MM — required when the day IS available.",
          ],
        ],
        example: `// 201 Created\n{ "override": { "id": "ov_2", "schedule_id": "sch_1a...", "override_date": "2026-08-15", "is_unavailable": true, "start_time": null, "end_time": null } }`,
      },
    ],
  },
  {
    id: "form",
    icon: FiFileText,
    title: "Booking form questions",
    endpoints: [
      {
        method: "GET",
        path: "/form-questions",
        auth: "jwt",
        summary: "List custom booking-form questions.",
        example: `{ "form_questions": [ { "id": "fq_1", "host_id": "3f9a...", "event_type_id": null, "label": "What's your goal?", "input_type": "text", "placeholder": null, "help_text": null, "required": true, "options": null, "sort_order": 0, "created_at": "2026-08-01T09:00:00.000Z" } ] }`,
      },
      {
        method: "POST",
        path: "/form-questions",
        auth: "jwt",
        summary: "Create a question.",
        body: [
          ["event_type_id", "uuid?", "Null = applies to all services."],
          ["label", "string", "1–240."],
          [
            "input_type",
            "enum",
            "text | paragraph | number | email | phone | select | radio | checkbox | date.",
          ],
          ["placeholder / help_text", "string?", "Nullable."],
          ["required", "boolean", "Default false."],
          ["options", "string[]?", "Required for select/radio/checkbox."],
          ["sort_order", "number", "Default 0."],
        ],
        example: `// 201 Created\n{ "form_question": { "id": "fq_2", "host_id": "3f9a...", "event_type_id": null, "label": "Preferred topic", "input_type": "select", "required": false, "options": ["Sales", "Support"], "sort_order": 1 } }`,
      },
      {
        method: "PATCH",
        path: "/form-questions/:id",
        auth: "jwt",
        summary: "Update a question (same shape as create).",
        example: `{ "form_question": { "id": "fq_2", "label": "Preferred topic", "input_type": "select", "options": ["Sales", "Support", "Billing"] } }`,
      },
      {
        method: "DELETE",
        path: "/form-questions/:id",
        auth: "jwt",
        summary: "Delete a question.",
        example: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "promos",
    icon: FiPercent,
    title: "Promo codes",
    endpoints: [
      {
        method: "GET",
        path: "/promo-codes",
        auth: "jwt",
        summary: "List the host’s promo codes.",
        example: `{ "promo_codes": [ ${EX_PROMO_ROW} ] }`,
      },
      {
        method: "POST",
        path: "/promo-codes",
        auth: "jwt",
        summary: "Create a promo code.",
        feature: "allow_paid_bookings",
        body: [
          ["code", "string", "2–40, A–Z 0–9 - _."],
          ["discount_type", "enum", "percentage | fixed."],
          ["discount_value", "number", "≥0 (≤100 for percentage)."],
          ["applicable_to", "enum", "all | paid_appointments_only, default all."],
          ["max_uses", "number?", "Nullable."],
          ["expires_at", "datetime?", "ISO 8601. Nullable."],
          ["is_disabled", "boolean", "Default false."],
        ],
        example: `// 201 Created\n{ "promo_code": ${EX_PROMO_ROW} }`,
      },
      {
        method: "PATCH",
        path: "/promo-codes/:id",
        auth: "jwt",
        summary: "Toggle disabled.",
        body: [["is_disabled", "boolean", ""]],
        example: `{ "promo_code": ${EX_PROMO_ROW} }`,
      },
      {
        method: "DELETE",
        path: "/promo-codes/:id",
        auth: "jwt",
        summary: "Delete a promo code.",
        example: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "bookings",
    icon: FiList,
    title: "Bookings (management)",
    endpoints: [
      {
        method: "GET",
        path: "/bookings",
        auth: "jwt",
        summary: "List/manage bookings.",
        query: [
          [
            "status",
            "string?",
            "hold | pending | awaiting_payment | confirmed | completed | cancelled.",
          ],
          ["from / to", "date?", "Date range (filters start_utc)."],
          ["page / per_page", "number?", "Pagination (default 25)."],
        ],
        example: `{ "bookings": [ ${EX_BOOKING_ROW} ] }`,
      },
      {
        method: "GET",
        path: "/bookings/kpis",
        auth: "jwt",
        summary: "Counts by status for the host.",
        example: `{ "status_breakdown": { "confirmed": 12, "completed": 30, "cancelled": 3 } }`,
      },
      {
        method: "GET",
        path: "/bookings/:id",
        auth: "jwt",
        summary:
          "Single booking + event title/duration + the invitee's form answers.",
        example: `{ "booking": { "id": "bk_7a8b...", "status": "confirmed", "start_utc": "2026-09-01T04:30:00.000Z", "end_utc": "2026-09-01T05:00:00.000Z", "invitee_name": "Jane Doe", "invitee_email": "jane@example.com", "event_title": "30 Minute Meeting", "duration_min": 30, "answers": [ { "label": "What's your goal?", "value": "Grow revenue" } ] } }`,
      },
      {
        method: "POST",
        path: "/bookings/:id/cancel",
        auth: "jwt",
        summary: "Cancel a booking.",
        example: `{ "booking": { "id": "bk_7a8b...", "status": "cancelled" } }`,
      },
      {
        method: "POST",
        path: "/bookings/:id/complete",
        auth: "jwt",
        summary: "Mark a booking completed.",
        example: `{ "booking": { "id": "bk_7a8b...", "status": "completed" } }`,
      },
    ],
  },
  {
    id: "domains",
    icon: FiGlobe,
    title: "Custom domains",
    blurb: "All gated by allow_custom_domains.",
    endpoints: [
      {
        method: "GET",
        path: "/domains",
        auth: "jwt",
        summary: "Get the configured custom domain + verification status.",
        feature: "allow_custom_domains",
        example: EX_DOMAIN,
      },
      {
        method: "POST",
        path: "/domains",
        auth: "jwt",
        summary: "Set a custom domain (resets verification, issues a TXT token).",
        feature: "allow_custom_domains",
        body: [["domain", "string", "e.g. book.acme.com"]],
        example: `// 201 Created\n${EX_DOMAIN}`,
      },
      {
        method: "POST",
        path: "/domains/verify",
        auth: "jwt",
        summary: "Check the DNS TXT record and mark verified.",
        feature: "allow_custom_domains",
        example: `{ "verified": true, "custom_domain": "book.acme.com" }`,
      },
      {
        method: "DELETE",
        path: "/domains",
        auth: "jwt",
        summary: "Remove the custom domain.",
        feature: "allow_custom_domains",
        example: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "agency",
    icon: FiUsers,
    title: "Team / Agency",
    blurb:
      "Context is available to owners and employees; employee management is owner-only.",
    endpoints: [
      {
        method: "GET",
        path: "/agency/context",
        auth: "jwt",
        summary: "Agency + role context for the current user.",
        example: `{ "role": "owner", "agency": { "id": "ag_1", "owner_id": "3f9a...", "slug": "acme", "name": "Acme Agency", "brand_name": "Acme" }, "membership": null }`,
      },
      {
        method: "PATCH",
        path: "/agency/me/password",
        auth: "jwt",
        summary: "Employee sets their own password after first sign-in.",
        body: [["password", "string", "Min 8."]],
        example: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/agency",
        auth: "jwt",
        summary: "Create the agency container (owner).",
        body: [
          ["name", "string", ""],
          ["slug", "string", ""],
          ["brand_name", "string?", "Optional."],
        ],
        example: `// 201 Created\n{ "id": "ag_1", "owner_id": "3f9a...", "slug": "acme", "name": "Acme Agency", "brand_name": "Acme", "created_at": "2026-08-20T09:00:00.000Z" }`,
      },
      {
        method: "GET",
        path: "/agency/employees",
        auth: "jwt",
        summary: "List employees + seat usage (owner).",
        example: `{
  "agency": { "id": "ag_1", "slug": "acme", "name": "Acme Agency" },
  "seats": { "used": 2, "max": 5 },
  "employees": [
    { "id": "m_1", "user_id": "u_9", "username": "riya", "display_name": "Riya", "priority": 0, "status": "active", "user_status": "active", "timezone": "Asia/Kolkata", "calendar_connected": true }
  ]
}`,
      },
      {
        method: "POST",
        path: "/agency/employees",
        auth: "jwt",
        summary:
          "Create an employee login (owner). Password optional — server generates one if omitted, returned once.",
        body: [
          ["display_name", "string", ""],
          ["username", "string", "Per-agency handle."],
          ["email", "string", "Sign-in identity."],
          ["password", "string?", "Min 8, optional."],
          ["timezone", "string?", "Optional."],
        ],
        example: `// 201 Created\n{ "employee": { "id": "m_2", "user_id": "u_10", "username": "arjun", "display_name": "Arjun", "status": "active" }, "credentials": { "username": "arjun", "password": "Xy7-temp-pass" } }`,
      },
      {
        method: "PATCH",
        path: "/agency/employees/:id",
        auth: "jwt",
        summary: "Edit employee (owner).",
        body: [
          ["display_name", "string?", ""],
          ["priority", "number?", "-100–100."],
          ["email", "string?", ""],
        ],
        example: `{ "id": "m_2", "user_id": "u_10", "username": "arjun", "display_name": "Arjun Rao", "priority": 10, "status": "active" }`,
      },
      {
        method: "POST",
        path: "/agency/employees/:id/reset-password",
        auth: "jwt",
        summary: "Reset employee password (owner).",
        body: [["password", "string", "Min 8."]],
        example: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/agency/employees/:id/suspend",
        auth: "jwt",
        summary: "Suspend an employee (owner).",
        example: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/agency/employees/:id/reactivate",
        auth: "jwt",
        summary: "Reactivate an employee (owner).",
        example: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/agency/employees/:id",
        auth: "jwt",
        summary: "Remove an employee (owner).",
        example: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "v1",
    icon: FiLock,
    title: "API v1 (API token, read-only)",
    blurb:
      "Server-to-server read API authorized with an API token (not a user JWT). Read-only in this release. Gated by allow_api_access. Prefix /api/v1. These are the only endpoints that wrap results in { data }.",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/me",
        auth: "token",
        summary: "Token owner (id, username, plan).",
        example: `{ "data": { "id": "3f9a...", "username": "acme", "plan": "Pro" } }`,
      },
      {
        method: "GET",
        path: "/api/v1/event-types",
        auth: "token",
        summary: "Active event types for the token owner.",
        example: `{ "data": [ ${EX_EVENT_TYPE_ROW} ] }`,
      },
      {
        method: "GET",
        path: "/api/v1/bookings",
        auth: "token",
        summary: "Bookings for the token owner.",
        query: [
          [
            "status",
            "string?",
            "hold | pending | awaiting_payment | confirmed | completed | cancelled.",
          ],
          ["from / to", "date?", "Date range."],
          ["page", "number?", "Default 1."],
          ["per_page", "number?", "Default 50, max 100."],
        ],
        example: `{ "data": [ ${EX_BOOKING_ROW} ] }`,
      },
    ],
  },
  {
    id: "api-tokens",
    icon: FiKey,
    title: "API token management",
    blurb:
      "Issue the Bearer tokens used by the API v1 above. Gated by allow_api_access.",
    endpoints: [
      {
        method: "GET",
        path: "/api-tokens",
        auth: "jwt",
        summary: "List issued tokens (metadata only — never the secret).",
        feature: "allow_api_access",
        example: `{ "api_tokens": [ { "id": "tok_1", "name": "Mobile app", "last_used_at": "2026-08-21T18:00:00.000Z", "revoked_at": null, "created_at": "2026-08-01T09:00:00.000Z" } ] }`,
      },
      {
        method: "POST",
        path: "/api-tokens",
        auth: "jwt",
        summary: "Create a token — the secret is returned ONCE (dck_ prefix).",
        feature: "allow_api_access",
        body: [["name", "string", "Label for the token."]],
        example: `// 201 Created — store "token" now, it is never shown again\n{ "token": "dck_9f8a7b6c5d...", "id": "tok_2", "name": "WordPress plugin" }`,
      },
      {
        method: "DELETE",
        path: "/api-tokens/:id",
        auth: "jwt",
        summary: "Revoke a token.",
        feature: "allow_api_access",
        example: `{ "ok": true }`,
      },
    ],
  },
];

const ADMIN_GROUPS = [
  {
    id: "admin-users",
    icon: FiUsers,
    title: "Users & plans",
    endpoints: [
      {
        method: "GET",
        path: "/admin/users",
        auth: "admin",
        summary: "List users (with plan name).",
        query: [
          ["q", "string?", "Search username/email/name."],
          ["page / per_page", "number?", "Pagination (default 25)."],
        ],
        example: `{ "users": [ { "id": "u_9", "username": "riya", "email": "riya@example.com", "name": "Riya", "role": "USER", "status": "active", "plan_id": "b2c7...", "plan_name": "Free", "is_primary_super_admin": false, "created_at": "2026-07-01T09:00:00.000Z" } ] }`,
      },
      {
        method: "POST",
        path: "/admin/users/:id/disable",
        auth: "admin",
        summary: "Disable a user (takes effect on their next request).",
        example: `{ "user": { "id": "u_9", "status": "disabled" } }`,
      },
      {
        method: "POST",
        path: "/admin/users/:id/enable",
        auth: "admin",
        summary: "Enable a user.",
        example: `{ "user": { "id": "u_9", "status": "active" } }`,
      },
      {
        method: "POST",
        path: "/admin/users/:id/change-plan",
        auth: "admin",
        summary: "Change a user’s plan (optionally time-boxed).",
        body: [
          ["plan_id", "uuid", ""],
          ["valid_days", "number?", "0/omitted = permanent."],
        ],
        example: `{ "user": { "id": "u_9", "plan_id": "pro-...", "plan_valid_until": "2026-09-21T00:00:00.000Z" } }`,
      },
      {
        method: "POST",
        path: "/admin/users/:id/reset-password",
        auth: "admin",
        summary: "Set a user’s password.",
        body: [["password", "string", "Min 8."]],
        example: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/admin/users/:id",
        auth: "admin",
        summary: "Delete a user (DPDP anonymize; financial history kept).",
        example: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/admin/plans",
        auth: "admin",
        summary: "Create a plan (all feature flags incl. allow_mobile_app).",
        example: `// 201 Created\n{ "plan": { "id": "pl_1", "name": "Pro", "price_display": "₹749", "max_event_types": -1, "allow_api_access": true, "allow_mobile_app": true } }`,
      },
      {
        method: "PATCH",
        path: "/admin/plans/:id",
        auth: "admin",
        summary: "Update a plan (partial).",
        example: `{ "plan": { "id": "pl_1", "name": "Pro", "allow_mobile_app": true } }`,
      },
    ],
  },
  {
    id: "admin-platform",
    icon: FiSettings,
    title: "Platform settings & promos",
    endpoints: [
      {
        method: "GET",
        path: "/admin/settings",
        auth: "admin",
        summary: "Platform settings (SMTP password is write-only).",
        example: `{ "settings": { "id": 1, "site_title": "DayCal", "default_locale": "en", "smtp_host": "smtp.example.com", "smtp_port": 587, "smtp_user": "mailer", "smtp_password_set": true, "upi_id": "acme@upi", "payment_qr_url": "https://.../qr.png" } }`,
      },
      {
        method: "PUT",
        path: "/admin/settings",
        auth: "admin",
        summary: "Update settings (title, locale, SMTP, payment config).",
        example: `{ "settings": { "id": 1, "site_title": "DayCal", "default_locale": "en", "smtp_password_set": true } }`,
      },
      {
        method: "POST",
        path: "/admin/payment-qr",
        auth: "admin",
        summary: 'Upload payment QR image. multipart, field "file".',
        example: `{ "payment_qr_url": "https://.../platform/payment-qr-1756800000.png" }`,
      },
      {
        method: "GET",
        path: "/admin/promo-codes",
        auth: "admin",
        summary: "List platform promo codes.",
        example: `{ "promo_codes": [ { "id": "pp_1", "scope": "platform", "host_id": null, "code": "LAUNCH20", "discount_type": "percentage", "discount_value": "20.00", "plan_id": null, "is_disabled": false } ] }`,
      },
      {
        method: "POST",
        path: "/admin/promo-codes",
        auth: "admin",
        summary: "Create a platform promo (optional plan_id scope).",
        example: `// 201 Created\n{ "promo_code": { "id": "pp_2", "scope": "platform", "code": "LAUNCH20", "discount_type": "percentage", "discount_value": "20.00" } }`,
      },
      {
        method: "PATCH",
        path: "/admin/promo-codes/:id",
        auth: "admin",
        summary: "Toggle disabled.",
        body: [["is_disabled", "boolean", ""]],
        example: `{ "promo_code": { "id": "pp_2", "code": "LAUNCH20", "is_disabled": true } }`,
      },
      {
        method: "DELETE",
        path: "/admin/promo-codes/:id",
        auth: "admin",
        summary: "Delete a platform promo.",
        example: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "admin-ops",
    icon: FiShield,
    title: "Bookings & audit",
    endpoints: [
      {
        method: "GET",
        path: "/admin/bookings",
        auth: "admin",
        summary: "Global bookings log (joined with host + event title).",
        query: [
          ["q", "string?", ""],
          ["page / per_page", "number?", ""],
        ],
        example: `{ "bookings": [ { "id": "bk_7a8b...", "status": "confirmed", "start_utc": "2026-09-01T04:30:00.000Z", "host_username": "acme", "host_name": "Acme Co", "host_email": "host@example.com", "event_title": "30 Minute Meeting" } ] }`,
      },
      {
        method: "POST",
        path: "/admin/bookings/:id/send-email",
        auth: "admin",
        summary: "Re-send a booking email (enqueues a job).",
        body: [
          [
            "template",
            "enum",
            "booking_confirmed | booking_cancelled | booking_reminder.",
          ],
        ],
        example: `{ "ok": true }`,
      },
      {
        method: "GET",
        path: "/admin/audit-logs",
        auth: "admin",
        summary: "Immutable audit trail.",
        query: [
          ["q", "string?", ""],
          ["page / per_page", "number?", ""],
        ],
        example: `{ "audit_logs": [ { "id": "al_1", "actor_user_id": "3f9a...", "action": "user.change_plan", "target_type": "user", "target_id": "u_9", "metadata": { "planId": "pro-...", "validDays": 30 }, "created_at": "2026-08-22T10:00:00.000Z" } ] }`,
      },
    ],
  },
];

const PUBLIC_GROUPS = [
  {
    id: "onboarding",
    icon: FiUser,
    title: "Signup & plans",
    endpoints: [
      {
        method: "POST",
        path: "/auth/signup",
        auth: "public",
        summary:
          "Create a new host account. Rate-limited and Turnstile-protected. Then sign in via Supabase Auth for a JWT.",
        body: [
          ["name", "string", "1–120."],
          ["username", "string", "3–40, a–z 0–9 - _."],
          ["email", "string", "Login email."],
          ["password", "string", "Min 8."],
        ],
        example: `// 201 Created\n{ "user": { "id": "3f9a...", "username": "acme", "email": "host@example.com", "name": "Acme Co" } }`,
      },
      {
        method: "GET",
        path: "/plans",
        auth: "public",
        summary: "List all plans (pricing table + feature flags).",
        example: `{ "plans": [ { "id": "pl_free", "name": "Free", "price_display": "₹0", "display_order": 0, "features_list": ["1 event type"], "max_event_types": 1, "allow_api_access": false, "allow_mobile_app": false }, { "id": "pl_pro", "name": "Pro", "price_display": "₹749", "allow_api_access": true, "allow_mobile_app": true } ] }`,
      },
    ],
  },
  {
    id: "booking-flow",
    icon: FiCalendar,
    title: "Booking flow",
    blurb:
      "Everything an app/plugin needs to render a booking: host → event type → slots → hold.",
    endpoints: [
      {
        method: "GET",
        path: "/public/hosts/:username",
        auth: "public",
        summary: "Public host profile + active event types.",
        example: `{
  "host": { "username": "acme", "name": "Acme Co", "brand_name": "Acme", "avatar_url": "https://.../avatar.png", "timezone": "Asia/Kolkata", "locale": "en" },
  "event_types": [ ${EX_EVENT_TYPE_ROW} ]
}`,
      },
      {
        method: "GET",
        path: "/public/hosts/:username/event-types/:slug",
        auth: "public",
        summary: "Public event-type details + its form questions.",
        example: `{
  "host": { "username": "acme", "name": "Acme Co", "brand_name": "Acme", "avatar_url": "https://.../avatar.png", "timezone": "Asia/Kolkata" },
  "event_type": ${EX_EVENT_TYPE_ROW},
  "form_questions": [ { "id": "fq_1", "label": "What's your goal?", "input_type": "text", "required": true, "options": null, "sort_order": 0 } ]
}`,
      },
      {
        method: "GET",
        path: "/public/slots",
        auth: "public",
        summary: "Bookable time slots for an event type on a date.",
        query: [
          ["event_type_id", "uuid", ""],
          ["date", "string", "YYYY-MM-DD."],
          ["timezone", "string?", "Invitee IANA tz."],
        ],
        example: `{
  "timezone": "Asia/Kolkata",
  "slots": ["2026-09-01T09:00:00+05:30", "2026-09-01T09:30:00+05:30"],
  "event_type": { "id": "e1d2...", "duration_min": 30, "require_payment": true, "price_minor": 1000, "currency": "INR" }
}`,
      },
      {
        method: "GET",
        path: "/public/availability-month",
        auth: "public",
        summary: "Which days in a month have availability (calendar dots).",
        query: [
          ["event_type_id", "uuid", ""],
          ["month", "string", "YYYY-MM."],
          ["timezone", "string?", "Invitee IANA tz."],
        ],
        example: `{
  "timezone": "Asia/Kolkata",
  "available_dates": ["2026-09-01", "2026-09-02", "2026-09-05"],
  "event_type": { "id": "e1d2...", "duration_min": 30, "require_payment": true, "price_minor": 1000, "currency": "INR" }
}`,
      },
      {
        method: "POST",
        path: "/public/holds",
        auth: "public",
        summary:
          "Reserve a slot and start the booking (step 1). Bot-checked & idempotent.",
        body: [
          ["event_type_id", "uuid", ""],
          ["start_utc", "datetime", "ISO 8601 with offset."],
          ["invitee_name", "string", "1–160."],
          ["invitee_email", "string", ""],
          ["invitee_timezone", "string?", "IANA tz."],
          [
            "answers",
            "array",
            "[{ question_label_snapshot, answer_value, form_question_id? }].",
          ],
          ["promo_code", "string?", "Nullable."],
          ["turnstile_token", "string", "Cloudflare Turnstile token (required)."],
        ],
        example: `// 201 Created\n{
  "booking": { "id": "bk_hold1", "status": "hold", "start_utc": "2026-09-01T04:30:00.000Z", "end_utc": "2026-09-01T05:00:00.000Z" },
  "confirmed": false,
  "requires_payment": true,
  "payment_available": true
}`,
      },
      {
        method: "POST",
        path: "/public/promo/validate",
        auth: "public",
        summary:
          "Validate a promo code against a paid event type. Returns the computed discount.",
        body: [
          ["code", "string", "Promo code."],
          [
            "event_type_id",
            "uuid",
            "The event being booked (must require payment).",
          ],
        ],
        example: `{ "valid": true, "originalMinor": 1000, "discountMinor": 100, "totalMinor": 900, "discount_type": "percentage" }`,
      },
      {
        method: "GET",
        path: "/public/host-by-domain",
        auth: "public",
        summary: "Resolve a VERIFIED custom domain to a host + event types.",
        query: [["domain", "string", ""]],
        example: `{
  "host": { "username": "acme", "name": "Acme Co", "brand_name": "Acme", "avatar_url": "https://.../avatar.png", "timezone": "Asia/Kolkata", "locale": "en" },
  "event_types": [ ${EX_EVENT_TYPE_ROW} ]
}`,
      },
      {
        method: "GET",
        path: "/public/payment-info",
        auth: "public",
        summary: "Manual payment config (UPI / bank / QR / WhatsApp).",
        example: `{ "payment_info": { "upi_id": "acme@upi", "bank_details": "A/C 000123... IFSC ...", "payment_qr_url": "https://.../qr.png", "whatsapp_number": "+919812345678" } }`,
      },
    ],
  },
];

const SYSTEM_GROUPS = [
  {
    id: "system",
    icon: FiServer,
    title: "Health, cron & webhooks",
    blurb:
      "Infra endpoints — listed for completeness. Do not call these from a mobile app or plugin.",
    endpoints: [
      {
        method: "GET",
        path: "/healthz",
        auth: "public",
        summary: "Health check (API + DB). 503 if the DB is down.",
        example: `{ "status": "ok", "db": "ok", "ts": "2026-08-22T10:00:00.000Z" }`,
      },
      {
        method: "GET",
        path: "/internal/cron/tick",
        auth: "system",
        summary:
          "Serverless queue drain. Guarded by CRON_SECRET; external scheduler only.",
      },
      {
        method: "GET",
        path: "/internal/cron/maintenance",
        auth: "system",
        summary: "Periodic maintenance. Guarded by CRON_SECRET.",
      },
      {
        method: "POST",
        path: "/webhooks/google",
        auth: "system",
        summary: "Inbound Google Calendar push. Signature-verified.",
      },
      {
        method: "POST",
        path: "/webhooks/razorpay",
        auth: "system",
        summary: "Inbound Razorpay events. Signature-verified.",
      },
      {
        method: "POST",
        path: "/webhooks/stripe",
        auth: "system",
        summary: "Inbound Stripe events. Signature-verified.",
      },
    ],
  },
];

const SECTIONS = [
  {
    id: "host-api",
    title: "Host API",
    accent: "brand",
    groups: HOST_GROUPS,
    blurb:
      "The main API a mobile app or plugin builds on. Authorized with a user JWT (Supabase Auth); a few read-only endpoints use an API token. Everything is scoped to the signed-in host.",
  },
  {
    id: "admin-api",
    title: "Super Admin API",
    accent: "danger",
    groups: ADMIN_GROUPS,
    blurb:
      "Platform-operator endpoints under /admin/*. Require a SUPER_ADMIN user JWT. Not needed by a host-facing app; every action is audited.",
  },
  {
    id: "public-api",
    title: "Public Booking API",
    accent: "neutral",
    groups: PUBLIC_GROUPS,
    blurb:
      "No authentication. Powers public onboarding and the visitor booking flow. Some writes require a Cloudflare Turnstile token.",
  },
  {
    id: "system-api",
    title: "System & Webhooks",
    accent: "warning",
    groups: SYSTEM_GROUPS,
    blurb: "Infrastructure — not part of the integration surface.",
  },
];

const ACCENT_BAR = {
  brand: "bg-brand-500",
  danger: "bg-red-500",
  neutral: "bg-slate-400",
  warning: "bg-amber-500",
};

const TOTAL = SECTIONS.reduce(
  (n, s) => n + s.groups.reduce((m, g) => m + g.endpoints.length, 0),
  0,
);
const countSection = (s) =>
  s.groups.reduce((m, g) => m + g.endpoints.length, 0);

// --- Postman collection generator -------------------------------------------------------
// Built from the SAME SECTIONS data so the exported collection never drifts from this page.
// Produces a Postman Collection v2.1 with folders (section → group → request), variables,
// per-request Bearer auth, sample bodies, and query params.

// A placeholder value for a request-body field, inferred from its "type" cell.
function sampleForField(name, type) {
  const t = (type || "").toLowerCase();
  if (t.startsWith("bool")) return true;
  if (t.startsWith("number")) return 0;
  if (t.startsWith("uuid")) return "00000000-0000-0000-0000-000000000000";
  if (t.startsWith("array")) return [];
  if (t.includes("datetime")) return new Date().toISOString();
  if (t.startsWith("enum")) return "";
  return `<${name}>`;
}

// Turn a body-field table into a sample JSON object (splits "a / b" combined labels).
function sampleBody(rows) {
  const obj = {};
  rows.forEach(([label, type]) => {
    label
      .split("/")
      .map((s) => s.trim())
      .forEach((key) => {
        if (key) obj[key.replace(/\?$/, "")] = sampleForField(key, type);
      });
  });
  return obj;
}

function authBlock(auth) {
  if (auth === "jwt" || auth === "admin") {
    return {
      type: "bearer",
      bearer: [{ key: "token", value: "{{userJwt}}", type: "string" }],
    };
  }
  if (auth === "token") {
    return {
      type: "bearer",
      bearer: [{ key: "token", value: "{{apiToken}}", type: "string" }],
    };
  }
  return { type: "noauth" };
}

function toPostmanRequest(ep) {
  const rawPath = ep.path; // e.g. /event-types/:id
  const segments = rawPath.split("/").filter(Boolean);
  const url = {
    raw: `{{baseUrl}}${rawPath}`,
    host: ["{{baseUrl}}"],
    path: segments,
  };
  if (ep.query) {
    url.query = ep.query.flatMap(([label]) =>
      label
        .split("/")
        .map((s) => s.trim().replace(/\?$/, ""))
        .filter(Boolean)
        .map((key) => ({ key, value: "", disabled: true })),
    );
  }
  const request = {
    method: ep.method,
    header: [],
    url,
    description: ep.summary,
    auth: authBlock(ep.auth),
  };
  if (ep.body) {
    request.header.push({ key: "Content-Type", value: "application/json" });
    request.body = {
      mode: "raw",
      raw: JSON.stringify(sampleBody(ep.body), null, 2),
      options: { raw: { language: "json" } },
    };
  }
  return { name: `${ep.method} ${ep.path}`, request };
}

function buildPostmanCollection() {
  // A convenience "Sign in" request so the team can fetch a user JWT via Supabase Auth.
  const authFolder = {
    name: "0. Get a token (Supabase Auth)",
    item: [
      {
        name: "POST Sign in → access_token",
        request: {
          method: "POST",
          header: [
            { key: "apikey", value: "{{supabaseAnonKey}}" },
            { key: "Content-Type", value: "application/json" },
          ],
          url: {
            raw: "{{supabaseUrl}}/auth/v1/token?grant_type=password",
            host: ["{{supabaseUrl}}"],
            path: ["auth", "v1", "token"],
            query: [{ key: "grant_type", value: "password" }],
          },
          body: {
            mode: "raw",
            raw: JSON.stringify(
              { email: "host@example.com", password: "••••••••" },
              null,
              2,
            ),
            options: { raw: { language: "json" } },
          },
          description:
            'Returns access_token — copy it into the collection variable "userJwt" to authorize the Host and Super Admin APIs.',
          auth: { type: "noauth" },
        },
      },
    ],
  };

  const folders = SECTIONS.map((s) => ({
    name: s.title,
    description: s.blurb,
    item: s.groups.map((g) => ({
      name: g.title,
      description: g.blurb,
      item: g.endpoints.map(toPostmanRequest),
    })),
  }));

  return {
    info: {
      name: "DayCal API",
      _postman_id: "00000000-0000-4000-8000-daycal000001",
      description:
        'DayCal REST API — Host, Super Admin, Public Booking, and System endpoints. Set the collection variables, run "Sign in" to get a userJwt, then call any endpoint. Generated from the in-app API docs.',
      schema:
        "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
    },
    variable: [
      { key: "baseUrl", value: API_BASE },
      { key: "supabaseUrl", value: SUPABASE_URL },
      { key: "supabaseAnonKey", value: "" },
      { key: "userJwt", value: "" },
      { key: "apiToken", value: "" },
    ],
    item: [authFolder, ...folders],
  };
}

function downloadPostmanCollection() {
  const json = JSON.stringify(buildPostmanCollection(), null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = "daycal-api.postman_collection.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function ProminentCard({ children, className = '' }) {
  return (
    <div className={`rounded-dc border border-slate-200/90 bg-white p-6 shadow-md shadow-slate-900/5 hover:shadow-lg transition-all ${className}`}>
      {children}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="flex items-center gap-1.5 rounded-dc-sm border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors"
    >
      {copied ? (
        <FiCheck className="h-3 w-3 text-emerald-500" />
      ) : (
        <FiCopy className="h-3 w-3" />
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function CodeBlock({ children }) {
  return (
    <div className="relative min-w-0">
      <div className="absolute right-2 top-2 z-10">
        <CopyButton text={children} />
      </div>
      <pre className="overflow-x-auto rounded-dc-sm bg-slate-900 p-4 pr-20 text-[12px] leading-relaxed text-slate-100">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function FieldTable({ title, rows }) {
  return (
    <div className="mt-3">
      <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {title}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <tbody className="text-slate-600">
            {rows.map((r) => (
              <tr key={r[0]} className="border-t border-slate-100 first:border-0">
                <td className="py-1.5 pr-4 align-top font-mono font-semibold text-slate-800 whitespace-nowrap">
                  {r[0]}
                </td>
                <td className="py-1.5 pr-4 align-top text-slate-500 whitespace-nowrap">
                  {r[1]}
                </td>
                <td className="py-1.5 align-top">{r[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Endpoint({ ep }) {
  const meta = AUTH_META[ep.auth];
  return (
    <div className="min-w-0 rounded-dc border border-slate-200/90 bg-white p-5 shadow-md shadow-slate-900/5 transition-all hover:shadow-lg hover:border-slate-300 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
        <div className="inline-flex items-center gap-2.5 rounded-dc-sm border border-slate-300/90 bg-slate-50 px-3 py-1.5 shadow-2xs">
          <Badge tone={methodTone(ep.method)}>{ep.method}</Badge>
          <code className="font-mono text-sm font-extrabold text-slate-900">
            {ep.path}
          </code>
        </div>
        <span className="ml-auto flex items-center gap-1.5">
          <Badge tone={meta.tone}>{meta.label}</Badge>
          {ep.feature && <Badge tone="warning">{ep.feature}</Badge>}
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-600 font-medium">{ep.summary}</p>
      {ep.query && <FieldTable title="Query parameters" rows={ep.query} />}
      {ep.body && <FieldTable title="Request body (JSON)" rows={ep.body} />}
      {ep.example && (
        <div className="mt-3">
          <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Example response
          </h4>
          <CodeBlock>{ep.example}</CodeBlock>
        </div>
      )}
    </div>
  );
}

function Group({ group }) {
  const { icon: Icon } = group;
  return (
    <div className="mt-6">
      <div className="mb-2.5 flex items-center gap-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-dc-sm bg-slate-100 text-slate-700">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h3 className="text-sm font-bold text-slate-900">{group.title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
          {group.endpoints.length}
        </span>
      </div>
      {group.blurb && (
        <p className="mb-2.5 max-w-3xl text-xs text-slate-500">{group.blurb}</p>
      )}
      <div className="grid min-w-0 gap-2.5">
        {group.endpoints.map((ep) => (
          <Endpoint key={ep.method + ep.path} ep={ep} />
        ))}
      </div>
    </div>
  );
}

function Section({ section }) {
  return (
    <section id={section.id} className="scroll-mt-6 mt-12 first:mt-8">
      <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
        <span
          className={`h-6 w-1.5 rounded-full ${ACCENT_BAR[section.accent]}`}
        />
        <h2 className="text-xl font-extrabold text-slate-900">
          {section.title}
        </h2>
        <Badge tone="neutral">{countSection(section)} endpoints</Badge>
      </div>
      {section.blurb && (
        <p className="mt-3 max-w-3xl text-sm text-slate-500">{section.blurb}</p>
      )}
      {section.groups.map((g) => (
        <Group key={g.id} group={g} />
      ))}
    </section>
  );
}

// --- Page -------------------------------------------------------------------------------

export default function AdminApiDocs() {
  return (
    <div className="max-w-4xl">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">API documentation</h1>
        <Badge tone="neutral">{TOTAL} endpoints</Badge>
        <button
          type="button"
          onClick={downloadPostmanCollection}
          className="ml-auto flex items-center gap-2 rounded-dc-sm bg-gradient-to-r from-slate-900 to-slate-800 px-3.5 py-2 text-xs font-semibold text-white shadow-md shadow-slate-900/10 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
        >
          <FiDownload className="h-3.5 w-3.5 text-brand-400" />
          Download Postman collection
        </button>
      </div>
      <p className="mb-4 max-w-3xl text-sm text-slate-500">
        The complete DayCal REST API, grouped by who uses it. Every endpoint
        includes a real response sample. Hand this reference (plus credentials)
        to a team building a mobile app, WordPress plugin, or any integration.
        All requests/responses are JSON unless noted (uploads are{" "}
        <code className="font-mono">multipart/form-data</code>).
      </p>

      {/* Centered High-Contrast Sticky Floating Capsule Quick Nav */}
      <div className="sticky top-4 z-30 mb-8 flex justify-center w-full pointer-events-none">
        <div className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-slate-900/95 p-1.5 shadow-2xl shadow-slate-900/30 border border-slate-700/80 ring-1 ring-white/10 backdrop-blur-xl max-w-full overflow-x-auto custom-scrollbar">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="group flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800/90 transition-all duration-200 shrink-0 cursor-pointer"
            >
              <span className={`h-2.5 w-2.5 rounded-full ${ACCENT_BAR[s.accent]}`} />
              <span>{s.title}</span>
              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-mono font-extrabold text-slate-400 group-hover:bg-slate-700 group-hover:text-white transition-colors">
                {countSection(s)}
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* Base URLs */}
      <ProminentCard>
        <FieldTable
          title="Base URLs"
          rows={[
            ["API", "REST", API_BASE],
            ["Auth", "Supabase", SUPABASE_URL],
          ]}
        />
      </ProminentCard>

      {/* Authentication guide */}
      <div className="mb-3 mt-10 flex items-center gap-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-dc-sm bg-slate-900 text-white">
          <FiKey className="h-4 w-4" />
        </span>
        <h2 className="text-lg font-bold text-slate-900">Authentication</h2>
      </div>
      <p className="mb-3 max-w-3xl text-sm text-slate-500">
        Every endpoint below carries a badge for how it’s authorized:{" "}
        <Badge tone="brand">User JWT</Badge> <Badge tone="success">API Token</Badge>{" "}
        <Badge tone="danger">Super Admin</Badge> <Badge tone="neutral">Public</Badge>
        .
      </p>

      <div className="grid gap-3">
        <ProminentCard>
          <div className="flex items-center gap-2">
            <Badge tone="brand">User JWT</Badge>
            <h3 className="text-sm font-bold text-slate-900">
              For the Host API (recommended for a mobile app)
            </h3>
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Sign the user in through Supabase Auth to get an{" "}
            <code className="font-mono">access_token</code>, then send it as a
            Bearer token on every API call. The token auto-expires — refresh it
            with the Supabase SDK. A <code className="font-mono">401</code> means
            the token is invalid/expired.
          </p>
          <div className="mt-3">
            <CodeBlock>{`# 1) Sign in (email + password) → access_token
curl -X POST "${SUPABASE_URL}/auth/v1/token?grant_type=password" \\
  -H "apikey: <SUPABASE_ANON_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{ "email": "host@example.com", "password": "••••••••" }'

# 2) Call the API with the returned access_token
curl "${API_BASE}/me" \\
  -H "Authorization: Bearer <ACCESS_TOKEN>"`}</CodeBlock>
          </div>
          <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <FiSmartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            Mobile-app access is a plan capability (
            <code className="font-mono">allow_mobile_app</code>). Grant it on the
            plan before onboarding an app.
          </p>
        </ProminentCard>

        <ProminentCard>
          <div className="flex items-center gap-2">
            <Badge tone="success">API Token</Badge>
            <h3 className="text-sm font-bold text-slate-900">
              For server-to-server (read-only /api/v1)
            </h3>
          </div>
          <p className="mt-2 text-sm text-slate-600">
            A host creates a token in their dashboard (Pro plan —{" "}
            <code className="font-mono">allow_api_access</code>). Use it for the{" "}
            <code className="font-mono">/api/v1/*</code> endpoints. Scoped to one
            host; read-only.
          </p>
          <div className="mt-3">
            <CodeBlock>{`curl "${API_BASE}/api/v1/me" \\
  -H "Authorization: Bearer <YOUR_API_TOKEN>"`}</CodeBlock>
          </div>
        </ProminentCard>

        <ProminentCard>
          <h3 className="text-sm font-bold text-slate-900">Conventions</h3>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-500">
            <li>
              • <code className="font-mono">/api/v1/*</code> responses wrap
              results as <code className="font-mono">{`{ "data": ... }`}</code>.
              Other endpoints return a named key for the resource — e.g.{" "}
              <code className="font-mono">{`{ "user" }`}</code>,{" "}
              <code className="font-mono">{`{ "event_types" }`}</code> /{" "}
              <code className="font-mono">{`{ "event_type" }`}</code>,{" "}
              <code className="font-mono">{`{ "bookings" }`}</code> /{" "}
              <code className="font-mono">{`{ "booking" }`}</code>.
            </li>
            <li>
              • Errors return{" "}
              <code className="font-mono">{`{ "error": { "message", "code?", "details?" } }`}</code>{" "}
              with an HTTP status.
            </li>
            <li>
              • Money is always integer <strong>minor units</strong> (paise),
              never floats.
            </li>
            <li>
              • Booking/payment writes accept an{" "}
              <code className="font-mono">Idempotency-Key</code> header.
            </li>
            <li>
              • <code className="font-mono">403</code> = plan/feature not
              enabled; <code className="font-mono">429</code> = rate-limited.
            </li>
          </ul>
        </ProminentCard>
      </div>

      {/* Sections by consumer */}
      {SECTIONS.map((s) => (
        <Section key={s.id} section={s} />
      ))}

      <p className="mt-12 text-center text-xs text-slate-400">
        This page documents the API contract only. Building the mobile app /
        plugin is the integrator’s responsibility.
      </p>
    </div>
  );
}
